/**
 * Generic money / date formatters shared across all apps (billing, admin, …).
 *
 * Money: cents → "$1,234.56" (USD symbol only; other currencies render no
 * symbol for now), or "—" when null/undefined.
 * Dates: ISO 8601 → "Apr 28, 2026" (and a date-time variant), or "—" when null.
 */
export const formatMoney = (cents, currency = 'usd') => {
	if (cents === null || cents === undefined) return '—';
	const value = Number(cents) / 100;
	const symbol = (currency || 'usd').toUpperCase() === 'USD' ? '$' : '';
	return `${symbol}${value.toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
};

export const formatDate = (iso) => {
	if (!iso) return '—';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '—';
	return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * An instant, rendered in a named zone when one is given and in the reader's
 * own zone when one is not.
 *
 * WHY A ZONE IS ACCEPTED AT ALL. Some documents are about a place rather than
 * about the reader: a school's ledger post-dates belong to the school's day,
 * not to the day the administrator reading them happens to be in. A payment
 * taken at 23:30 in Los Angeles on 31 August reads "Sep 1, 2:30 AM" to a
 * reader in New York, which places it outside a statement period the same page
 * prints as ending 31 August.
 *
 * `timeZone` is optional and the default is unchanged, so every existing caller
 * keeps the reader's zone — which is right for anything that really is about
 * the reader.
 *
 * AN UNUSABLE ZONE FALLS BACK RATHER THAN THROWING. The value arrives from an
 * API, which makes it a system boundary; `toLocaleDateString` answers an
 * unrecognised zone with a RangeError, and one bad field would otherwise take
 * the whole document down rather than one cell.
 *
 * @param {string|null|undefined} iso
 * @param {string} [timeZone] an IANA zone name, e.g. `America/Los_Angeles`.
 * @returns {string}
 */
/** The shape an endpoint emits when it means a calendar day and not an instant. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A date-only value from the API, rendered as the calendar day it names.
 *
 * WHY THIS IS NOT `formatDate`. The shared formatter is `new Date(iso)` followed
 * by `toLocaleDateString`, and ECMAScript parses a date-ONLY string as a UTC
 * instant. At any negative UTC offset the browser then renders the day BEFORE
 * the one the endpoint sent: `2026-08-10` reads `Aug 9, 2026` in
 * America/New_York. Measured in a browser, not reasoned about. Every operator
 * west of UTC reads every date-only cell one day early, on the happy path.
 *
 * So a `YYYY-MM-DD` input is rebuilt from its own parts as a LOCAL calendar
 * date, which has no zone left to re-zone, and the rendering is then DELEGATED
 * to the shared formatter. Delegating rather than re-spelling
 * `toLocaleDateString` is the point: the month/day/year options have exactly one
 * definition, so a date-only cell and a date-time cell on the same screen cannot
 * drift apart.
 *
 * Anything else — an ISO-8601 instant, a null, an unparseable string — goes to
 * the shared formatter unchanged. A date-time value genuinely IS an instant and
 * wants the browser's zone. This narrows the one shape where that reasoning does
 * not hold and narrows nothing else, which is why it lives here rather than in
 * `@shared/helpers/format`.
 *
 * WHY THIS IS NOT NAMED `formatCalendarDate`. The sibling module `./datePresets`
 * already exports that name for a hand-built, always-English rendering of the
 * same input shape, and `DepositReportPage` imports it. Two functions with one
 * name in one `utils/` directory, disagreeing outside `en-US`, is the drift this
 * function's delegation exists to prevent — so it carries the name nothing else
 * in the directory claims.
 *
 * @param {string|null|undefined} value
 * @returns {string}
 */
export const formatDateOnly = (value) => {
	if (typeof value !== 'string' || !DATE_ONLY.test(value)) return formatDate(value);
	const [year, month, day] = value.split('-').map(Number);
	const local = new Date(year, month - 1, day);
	// THE PATTERN ADMITS IMPOSSIBLE DAYS, AND THE LANGUAGE DOES NOT REJECT THEM.
	// `2026-02-31` is structurally valid, and `new Date(2026, 1, 31)` is a VALID
	// Date by ECMAScript's month/day overflow rule — it rolls over to 3 March. So
	// without this check a nonsense value would render as a real but WRONG day.
	// Reading the parts back out is the whole test.
	//
	// AND THE REJECTED VALUE IS NOT HANDED BACK TO `formatDate`. This comment
	// used to claim the shared formatter answers an em-dash for the same string,
	// and it does not: `new Date('2026-02-31')` rolls over in V8 exactly as the
	// component form does, so delegating produced the same wrong-but-plausible
	// day the check exists to prevent. The em-dash is returned here, which is
	// what the shared formatter renders for every other input it cannot make
	// sense of.
	if (local.getFullYear() !== year || local.getMonth() !== month - 1 || local.getDate() !== day) {
		return '—';
	}
	return formatDate(local);
};

export const formatDateTime = (iso, timeZone) => {
	if (!iso) return '—';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '—';
	const zone = timeZone ? { timeZone } : {};
	const render = (options) =>
		`${d.toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			...options,
		})}, ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', ...options })}`;
	try {
		return render(zone);
	} catch {
		return render({});
	}
};
